import { useCallback, useEffect, useState } from 'react';
import type { CeebBotMeta } from '../components/CeebBotReport';
import { authedFetch, API_BASE_URL } from '../services/api';

// ceeb-demo/* nie ma wrappera w services/api.ts (kontrakt-API), ale base URL bierzemy
// z kanonu API_BASE_URL (eksport ADDYTYWNY z api.ts — unifikacja 2026-07-28); request
// idzie przez authedFetch (Bearer jak reszta klienta — bez tokenu przy AUTH_ENFORCE=1
// backend zwracał 401, a hook chował go po cichu; W2).

export interface LastCeebMeta extends CeebBotMeta {
  inspectionId?: string | null;
  finishedAt?: string | null;
}

export interface UseLastCeebMeta {
  /** Meta ostatniego runu bota — TYLKO gdy meta.inspectionId === żądany inspectionId. */
  meta: LastCeebMeta | null;
  /** Ręczne odświeżenie (np. zaraz po zakończeniu pushu w tym samym widoku). */
  refetch: () => Promise<void>;
}

/**
 * useLastCeebMeta — czyta GET /api/ceeb-demo/last-meta.json: SINGLETON plik nadpisywany
 * PRZY KAŻDYM uruchomieniu bota `fillCeebDraft.js` (fs.writeFileSync na końcu runu —
 * backend/src/scripts/fillCeebDraft.js), niezależnie od tego, kto go odpalił: „Wyślij do
 * CEEB" (POST /buildings/:id/ceeb-push, PipelineView.handlePushToCeeb) czy demo
 * (POST /ceeb-demo/fill, CeebDemoButton). Ten sam plik czyta też backend/src/routes/
 * ceeb-sync.js (BOT_META_PATH) do oznaczania ceeb_synced_at — więc kształt/świeżość są
 * już tam zweryfikowane po stronie backendu (analogicznie robimy to i tu, po stronie UI).
 *
 * Plik jest GLOBALNY i nadpisywany przy każdym runie — dlatego `meta` zwracamy TYLKO
 * gdy `meta.inspectionId` zgadza się z żądanym `inspectionId` ORAZ run jest zakończony
 * (`finishedAt` obecny). Inaczej raport z INNEGO budynku/runu (albo brak run-a wcale)
 * mógłby się „przykleić" do niewłaściwego widoku (#51b).
 */
export function useLastCeebMeta(inspectionId: string | null | undefined): UseLastCeebMeta {
  const [meta, setMeta] = useState<LastCeebMeta | null>(null);

  const refetch = useCallback(async () => {
    if (!inspectionId) {
      setMeta(null);
      return;
    }
    try {
      const r = await authedFetch(`${API_BASE_URL}/ceeb-demo/last-meta.json?t=${Date.now()}`);
      const data: LastCeebMeta = await r.json();
      setMeta(data && data.finishedAt && data.inspectionId === inspectionId ? data : null);
    } catch {
      // Cichy błąd — raport to wzbogacenie widoku, nie krytyczna ścieżka.
      setMeta(null);
    }
  }, [inspectionId]);

  useEffect(() => {
    // Reset NATYCHMIAST przy zmianie inspectionId — bez „przebłysku" starego raportu,
    // zanim nowy fetch się rozliczy.
    setMeta(null);
    void refetch();
  }, [inspectionId, refetch]);

  return { meta, refetch };
}
